import { useEffect, useRef, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Status } from "@repo/database";

import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useProduct } from "~/hooks/use-product";
import { useRegisterStore } from "~/stores/use-register-store";
import { RegisterButton } from "../ui/register-button";
import { CategoryButton } from "./category-button";

export function CategoryList({
  onCategorySelect,
}: {
  onCategorySelect?: (categoryId: string) => void;
}) {
  const { productCategories } = useProduct();

  const { setSelectedCategoryId, selectedCategoryId } = useRegisterStore();

  const [ref] = useDragScroll();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollAmount = 80; // Amount to scroll in pixels

  const [isScrollableUp, setIsScrollableUp] = useState(false);

  const [isScrollableDown, setIsScrollableDown] = useState(false);

  const activeProductCategories = productCategories.filter(
    (p) => p.status === Status.active,
  );

  const lastCategoryPosition = activeProductCategories.reduce(
    (acc, curr) => Math.max(acc, curr.position),
    0,
  );

  const handleScroll = (dir: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        top: dir === -1 ? -scrollAmount : scrollAmount,
      });
      checkScrollable(); // Check scrollable status after scrolling
    }
  };

  const checkScrollable = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setIsScrollableUp(scrollTop > 0);
      setIsScrollableDown(scrollTop + clientHeight < scrollHeight);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      checkScrollable();

      scrollElement.addEventListener("scroll", checkScrollable);

      window.addEventListener("resize", checkScrollable);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
    };
  }, []);

  useEffect(() => {
    if (selectedCategoryId) return;
    const firstCategory = activeProductCategories.sort(
      (a, b) => a.position - b.position,
    )[0];
    if (firstCategory) {
      setSelectedCategoryId(firstCategory.id);
    }
  }, [activeProductCategories, setSelectedCategoryId, selectedCategoryId]);

  return (
    <div className="flex h-full flex-col gap-0.5 overflow-hidden">
      <div className="border-b-2">
        <RegisterButton
          className="items-center w-full justify-center rounded-none h-14"
          color="#000000"
          disabled={!isScrollableUp}
          onClick={() => {
            handleScroll(-1);
          }}
        >
          <ArrowUpwardIcon />
        </RegisterButton>
      </div>

      <div
        className="hide-scrollbar flex-1 grid grid-cols-2 @md:grid-cols-1 gap-1 content-start overflow-auto p-1"
        ref={(e) => {
          ref(e);
          scrollRef.current = e;
        }}
      >
        {Array.from({ length: lastCategoryPosition }).map((_, index) => {
          const productCategory = activeProductCategories.find(
            (p) => p.position === index + 1,
          );
          if (productCategory) {
            return (
              <CategoryButton
                disabled={productCategory.status === Status.inactive}
                className="min-h-14 max-h-14"
                key={productCategory.id}
                onClick={() => {
                  if (onCategorySelect) {
                    onCategorySelect(productCategory.id);
                  }
                }}
                productCategory={productCategory}
              />
            );
          }
          return <RegisterButton className="min-h-14" key={index} />;
        })}
      </div>

      <div className="border-t-2">
        <RegisterButton
          className="items-center justify-center rounded-none h-14 w-full"
          color="#000000"
          disabled={!isScrollableDown}
          onClick={() => {
            handleScroll(1);
          }}
        >
          <ArrowDownwardIcon />
        </RegisterButton>
      </div>
    </div>
  );
}
